import ExpiringLeasesImg from '~/assets/images/custom-layout/home/expiringLeases.png';
import LeasesWithBalanceDueImg from '~/assets/images/custom-layout/home/leasesWithBalanceDue.png';
import MaintenanceRequestsImg from '~/assets/images/custom-layout/home/maintenaceRequests.png';
import RentApplicationsImg from '~/assets/images/custom-layout/home/rentApplications.png';
import { HOA_DASHBOARD_TITLE_OVERRIDES, isHoaDashboardModuleHidden, IS_HOA_DEMO_MODE } from '~/utils/hoaDemoMode';
import type { DashboardModuleDefinition, DashboardModuleKey, DashboardWidgetPlacement } from '~/config/dashboard/types';

export const DASHBOARD_LAYOUT_STORAGE_KEY = 'dashboard-layout-v7';
export const DASHBOARD_LIST_CARD_HEIGHT = 432;

const placement = (
  key: DashboardModuleKey,
  sectionId: DashboardWidgetPlacement['sectionId'],
  x: number,
  width: number,
  layoutOrder: number,
  mobileOrder: number,
  fillEmptySpace = false
): DashboardWidgetPlacement => ({ key, sectionId, x, width, preferredRow: 0, fillEmptySpace, layoutOrder, mobileOrder });

const definitions: DashboardModuleDefinition[] = [
  {
    key: 'OnlinePaymentSales',
    thumbnail: LeasesWithBalanceDueImg,
    title: 'Get paid faster',
    description: 'Compare your online payment rate to the platform and convert tenants over with one click',
    defaultPlacement: placement('OnlinePaymentSales', 'hero', 0, 12, 0, 0),
    minWidth: 6,
    maxWidth: 12,
    heightPolicy: { mode: 'intrinsic' },
    mandatory: true,
    permissions: { resource: 'leases', action: 'read' },
  },
  {
    key: 'Insurance',
    thumbnail: LeasesWithBalanceDueImg,
    title: 'Insurance',
    description: 'Renters insurance coverage overview and Notice Campaign',
    defaultPlacement: placement('Insurance', 'highlights', 0, 7, 0, 1),
    minWidth: 4,
    maxWidth: 12,
    heightPolicy: { mode: 'intrinsic' },
    permissions: { resource: 'leases', action: 'read' },
  },
  {
    key: 'OnTimeRent',
    thumbnail: LeasesWithBalanceDueImg,
    title: 'On-time rent',
    description: 'Share of rent charges paid by their grace deadline in the last 30 days',
    defaultPlacement: placement('OnTimeRent', 'highlights', 7, 5, 1, 2),
    minWidth: 4,
    maxWidth: 12,
    heightPolicy: { mode: 'intrinsic' },
    permissions: { resource: 'leases', action: 'read' },
  },
  {
    key: 'PaidSupport',
    thumbnail: LeasesWithBalanceDueImg,
    title: 'Expert setup support',
    description: 'Get hands-on help from our team to set up your portal',
    defaultPlacement: placement('PaidSupport', 'highlights', 0, 7, 2, 3, true),
    minWidth: 4,
    maxWidth: 12,
    heightPolicy: { mode: 'intrinsic' },
  },
  {
    key: 'DailyMoneyMovement',
    thumbnail: LeasesWithBalanceDueImg,
    title: 'Cash flow',
    description: 'Daily money in and out for the last 30 days',
    defaultPlacement: placement('DailyMoneyMovement', 'highlights', 7, 5, 3, 4, true),
    minWidth: 4,
    maxWidth: 12,
    heightPolicy: { mode: 'intrinsic' },
    permissions: { resource: 'transactions', action: 'read' },
  },
  {
    key: 'ReturnsAndDisputes',
    thumbnail: LeasesWithBalanceDueImg,
    title: 'Returns & disputes',
    description: 'ACH returns and card disputes in the last 30 days',
    defaultPlacement: placement('ReturnsAndDisputes', 'operations', 0, 4, 0, 5),
    minWidth: 3,
    maxWidth: 12,
    heightPolicy: { mode: 'fixed', height: DASHBOARD_LIST_CARD_HEIGHT },
    permissions: { resource: 'transactions', action: 'read' },
  },
  {
    key: 'LeasesWithBalanceDue',
    thumbnail: LeasesWithBalanceDueImg,
    title: 'Leases with balance due',
    description: 'Display the most recent  balance payable on the home page',
    defaultPlacement: placement('LeasesWithBalanceDue', 'operations', 4, 8, 1, 6),
    minWidth: 4,
    maxWidth: 12,
    heightPolicy: { mode: 'fixed', height: DASHBOARD_LIST_CARD_HEIGHT },
    permissions: { resource: 'leases', action: 'read' },
  },
  {
    key: 'RentalApplications',
    thumbnail: RentApplicationsImg,
    title: 'Rental applications',
    description: 'Preview recent applications on the home page',
    defaultPlacement: placement('RentalApplications', 'operations', 0, 4, 2, 7),
    minWidth: 3,
    maxWidth: 12,
    heightPolicy: { mode: 'fixed', height: DASHBOARD_LIST_CARD_HEIGHT },
    permissions: { resource: 'rental_applications', action: 'read' },
  },
  {
    key: 'MaintenanceRequests',
    thumbnail: MaintenanceRequestsImg,
    title: 'Maintenance requests',
    description: 'Displays the latest maintenance request',
    defaultPlacement: placement('MaintenanceRequests', 'operations', 4, 4, 3, 8),
    minWidth: 3,
    maxWidth: 12,
    heightPolicy: { mode: 'fixed', height: DASHBOARD_LIST_CARD_HEIGHT },
    permissions: { resource: 'maintenance_requests', action: 'read' },
  },
  {
    key: 'UnreadMessages',
    thumbnail: ExpiringLeasesImg,
    title: 'Unread messages',
    description: 'Displays the latest unread messages',
    defaultPlacement: placement('UnreadMessages', 'operations', 8, 4, 4, 9),
    minWidth: 3,
    maxWidth: 12,
    heightPolicy: { mode: 'fixed', height: DASHBOARD_LIST_CARD_HEIGHT },
    permissions: { resource: 'chats', action: 'read' },
  },
];

export const getDashboardModuleRegistry = (): DashboardModuleDefinition[] => {
  const visible = definitions.filter((item) => !isHoaDashboardModuleHidden(item.key));
  if (!IS_HOA_DEMO_MODE) return visible;
  return visible.map((item) => {
    const overrides = HOA_DASHBOARD_TITLE_OVERRIDES[item.key];
    return overrides ? { ...item, ...overrides } : item;
  });
};
